// =============================================================================
// Receita — a "Visão Executiva" do protótipo
//
// Duas grandezas. RECEITA ORÇADA desde a migration 043: a `v_categoria_orcavel`
// aceita `natureza = 'receita'`, os quatro caminhos de escrita do orçamento
// gravam a linha, e `v_receita_orcada_mes` a devolve sem contaminar nenhuma
// agregação de despesa.
//
// RECEITA REALIZADA desde a 061 (14/10): contas a receber do Conta Azul, lida
// na mesma rodada da despesa (docs/SPIKE_CONTA_AZUL.md S9). O Financeiro a lança
// agregada por produto e gateway, e quase nunca com centro de custo — por isso
// ela é lida por CATEGORIA, não por CC. Realizado é o RECEBIDO, como na despesa
// (pago); o lançado na competência e o a receber vêm ao lado.
//
// ⚠️ Os cartões de resultado seguem sem número, e agora não é por falta de
// dado: resultado é receita − despesa, e quais deduções entram (imposto sobre
// venda, estorno, taxa de gateway) é a DRE da Frente 2, ainda não definida com
// o Financeiro. Um resultado com a regra errada seria um número plausível e
// falso — tela que mente é pior que tela ausente, então o que falta aparece
// como "—", dizendo de onde viria.
//
// E o AF-01 §10 mapeia esta aba para a **Visão Executiva**, com status
// "enquadramento proposto" — ou seja, Frente 2, não P1.
// =============================================================================

import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { buscarReceita, type ReceitaOrcada } from "../lib/api.ts";
import { emReais, moeda, moedaCompacta, mesCurto } from "../lib/formato.ts";
import {
  BarraFiltros,
  CabecalhoGrafico,
  Campo,
  COR,
  Kpi,
  LegendaGrafico,
  Painel,
  TooltipRico,
  Vazio,
} from "../componentes/ui.tsx";
import { FiltroAno, FiltroMes } from "../componentes/FiltroPeriodo.tsx";

/** Os cartões que ainda não têm número, com a origem de cada um. */
const SEM_DADO: [string, string][] = [
  ["Receita projetada (fim do ano)", "Run-rate dos últimos 3 meses de receita"],
  ["Resultado orçado", "Receita orçada − despesa orçada"],
  ["Resultado projetado (fim do ano)", "Receita projetada − despesa projetada"],
  ["Desvio acumulado (resultado)", "Resultado realizado − resultado orçado"],
];

export function Receita({
  periodo,
  onMudarPeriodo,
}: {
  periodo: string[];
  onMudarPeriodo: (p: string[]) => void;
}) {
  const q = useQuery<ReceitaOrcada>({
    queryKey: ["receita", periodo],
    queryFn: () => buscarReceita(periodo),
  });

  const orcadoPeriodo = q.data?.consolidado?.orcado_centavos ?? null;
  const realizado = q.data?.realizado ?? null;
  const serie = (q.data?.serie_ano ?? []).map((p) => ({
    mes: mesCurto(p.competencia),
    orcado: emReais(p.orcado_centavos),
    // Nulo no mês sem lançamento: mês futuro não é receita zero.
    realizado: p.recebido_centavos === null ? null : emReais(p.recebido_centavos),
  }));
  const somaAno = (campo: "orcado_centavos" | "recebido_centavos") =>
    (q.data?.serie_ano ?? []).reduce((a, p) => a + BigInt(p[campo] ?? "0"), 0n).toString();
  const orcadoAno = somaAno("orcado_centavos");
  const recebidoAno = somaAno("recebido_centavos");
  const temReceita = orcadoAno !== "0" || (q.data?.serie_ano ?? []).some((p) => p.recebido_centavos !== null);
  const categorias = q.data?.categorias ?? [];
  // Barras proporcionais à maior categoria, pelo maior dos dois valores.
  const maiorCategoria = Math.max(
    1,
    ...categorias.map((c) => Math.max(emReais(c.orcado_centavos), emReais(c.recebido_centavos ?? "0"))),
  );

  return (
    <div className="pagina pb-20">
      <header className="pt-7 pb-5">
        <p className="text-[11px] font-semibold tracking-[0.11em] text-tinta-fraca uppercase">
          Visão Executiva
        </p>
        <h1 className="mt-1 font-display text-[22px] leading-tight font-bold tracking-tight">
          Receita e resultado
        </h1>
        <p className="mt-1 text-[14px] text-tinta-fraca">
          Receita orçada, realizada e projetada, com o resultado consolidado do período.
        </p>
      </header>

      {/* A "Nota de escopo" do protótipo saiu em 21/10, a pedido do Financeiro.
          O que ela dizia continua valendo: a receita realizada vem por categoria
          (o Conta Azul a traz quase sempre sem CC), realizado é o recebido, e os
          indicadores de resultado esperam a DRE da Frente 2 definir as deduções. */}
      <BarraFiltros className="mb-6">
        <Campo rotulo="Mês">
          <FiltroMes periodo={periodo} onMudar={onMudarPeriodo} />
        </Campo>
        <Campo rotulo="Ano">
          <FiltroAno periodo={periodo} onMudar={onMudarPeriodo} />
        </Campo>
      </BarraFiltros>

      {/* Os seis cartões do protótipo, na ordem dele. Os dois primeiros têm
          número; os outros quatro dizem de onde o número viria. */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(215px,1fr))] gap-3.5">
        <Kpi
          rotulo="Receita orçada"
          valor={q.isLoading ? "…" : moeda(orcadoPeriodo)}
          apoio={
            periodo.length > 1
              ? `${periodo.length} meses selecionados`
              : "Linha de receita no orçamento do exercício"
          }
          dica="Soma das linhas orçamentárias de categorias de receita no período selecionado. Recortada pelos seus centros de custo."
          destaque
          comparacoes={[
            { rotulo: "No ano", valor: moeda(orcadoAno) },
            {
              rotulo: "Categorias",
              valor: String(q.data?.consolidado?.qtd_categorias ?? 0),
            },
          ]}
        />
        <Kpi
          rotulo="Receita realizada"
          valor={q.isLoading ? "…" : moeda(realizado?.recebido_centavos ?? "0")}
          apoio={
            realizado && realizado.qtd_lancamentos > 0
              ? "Recebida no período, pela competência do lançamento"
              : "Nenhum recebimento lançado no período"
          }
          dica="Contas a receber do Conta Azul na competência do período, pelo que já foi recebido — o mesmo critério do realizado de despesa. Lançado é o valor da competência, recebido ou não. Por categoria: o Conta Azul traz a receita quase sempre sem centro de custo."
          comparacoes={[
            { rotulo: "Lançado", valor: moeda(realizado?.lancado_centavos ?? "0") },
            { rotulo: "A receber", valor: moeda(realizado?.a_receber_centavos ?? "0") },
          ]}
        />
        {SEM_DADO.map(([rotulo, origem]) => (
          <div
            key={rotulo}
            className="rounded-xl border border-linha border-l-4 border-l-neutro-linha bg-superficie px-[18px] py-4 shadow-[0_1px_2px_rgb(16_40_80_/_0.04)]"
          >
            <span className="text-[11px] font-semibold tracking-wide text-tinta-fraca uppercase">
              {rotulo}
            </span>
            <div className="num mt-1.5 font-display text-[21px] leading-none font-bold text-tinta-fraca">
              —
            </div>
            <p className="mt-2 text-[11px] leading-snug text-tinta-fraca">Origem: {origem}</p>
          </div>
        ))}
      </div>

      <Painel className="mt-6">
        <CabecalhoGrafico
          titulo={`Receita por mês — ${q.data?.exercicio ?? ""}`}
          dica={`Orçado e realizado (recebido) por competência do exercício. No ano: ${moeda(orcadoAno)} orçados e ${moeda(recebidoAno)} recebidos. Mês sem lançamento fica sem coluna de realizado, em vez de zero.`}
        />
        {!temReceita ? (
          <Vazio
            titulo="Nenhuma receita neste exercício"
            dica="Sem linha de receita no orçamento e sem contas a receber no Conta Azul. Cadastre a linha de receita pela Planilha, no filtro Natureza → Receita, ou pela aba Cadastrar escolhendo uma categoria do grupo Receita."
          />
        ) : (
          <>
            <div className="h-[280px] w-full">
              <ResponsiveContainer>
                <BarChart data={serie} margin={{ top: 8, right: 8, left: 8 }}>
                  <CartesianGrid stroke="#eef1f6" vertical={false} />
                  <XAxis
                    dataKey="mes"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 11, fill: "#6b7789" }}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    width={68}
                    tick={{ fontSize: 11, fill: "#6b7789" }}
                    tickFormatter={(v: number) => moedaCompacta(String(Math.round(v * 100)))}
                  />
                  <Tooltip
                    cursor={{ fill: "rgba(16,40,80,0.04)" }}
                    content={({ active, payload, label }) =>
                      active && payload?.length ? (
                        <TooltipRico
                          titulo={String(label)}
                          itens={payload.filter((p) => p.value !== null && p.value !== undefined).map((p) => ({
                            rotulo: p.dataKey === "realizado" ? "Realizado (recebido)" : "Orçado",
                            cor: p.color,
                            valor: (p.value as number).toLocaleString("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                            }),
                          }))}
                        />
                      ) : null
                    }
                  />
                  <Bar dataKey="orcado" fill={COR.orcado} radius={[2, 2, 0, 0]} maxBarSize={22} />
                  <Bar dataKey="realizado" fill={COR.realizado} radius={[2, 2, 0, 0]} maxBarSize={22} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            {/* FORA do contêiner de altura fixa, senão o ResponsiveContainer a
                empurra para fora do cartão. */}
            <LegendaGrafico series={[["Orçado", COR.orcado], ["Realizado (recebido)", COR.realizado]]} />
          </>
        )}
      </Painel>

      <Painel className="mt-6">
        <CabecalhoGrafico
          titulo="Receita por categoria"
          dica="As categorias de receita do período selecionado, com o realizado (recebido) e o orçado, da maior para a menor. Barras proporcionais à maior categoria, não ao total — senão uma categoria dominante achata todas as outras."
        />
        {categorias.length === 0 ? (
          <Vazio titulo="Sem receita no período selecionado" />
        ) : (
          <>
            <ul className="mt-1 space-y-3">
              {categorias.map((c) => {
                const recebido = c.recebido_centavos ?? "0";
                const temOrcado = c.orcado_centavos !== "0";
                const aReceber = c.a_receber_centavos && c.a_receber_centavos !== "0" ? c.a_receber_centavos : null;
                return (
                  <li key={c.categoria_id ?? "sem-categoria"}>
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3 text-[13px]">
                      <span className="min-w-0 truncate text-tinta-media" title={c.categoria_nome}>
                        {c.categoria_codigo ? `${c.categoria_codigo} · ` : ""}
                        {c.categoria_nome}
                      </span>
                      <span className="num shrink-0">
                        <span className="font-semibold">{moeda(recebido)}</span>
                        {temOrcado && <span className="text-tinta-fraca"> de {moeda(c.orcado_centavos)}</span>}
                        {aReceber && <span className="text-tinta-fraca"> · {moeda(aReceber)} a receber</span>}
                      </span>
                    </div>
                    <div className="mt-1 space-y-0.5">
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-superficie-2">
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${Math.max(0, (emReais(recebido) / maiorCategoria) * 100)}%`, background: COR.realizado }}
                        />
                      </div>
                      {temOrcado && (
                        <div className="h-1 w-full overflow-hidden rounded-full bg-superficie-2">
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${(emReais(c.orcado_centavos) / maiorCategoria) * 100}%`, background: COR.orcado }}
                          />
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
            <LegendaGrafico series={[["Realizado (recebido)", COR.realizado], ["Orçado", COR.orcado]]} />
          </>
        )}
      </Painel>

      <Painel className="mt-6">
        <CabecalhoGrafico
          titulo="Resultado acumulado"
          dica="Resultado orçado, realizado e projetado (receita − despesa), acumulados de janeiro a dezembro."
        />
        <Vazio
          titulo="Depende da regra da DRE"
          dica="Os dois lados já estão no sistema, despesa e receita, orçadas e realizadas. Falta a regra: quais deduções saem da receita antes do resultado (imposto sobre venda, estorno, taxa de gateway), que é a DRE da Frente 2. Um resultado com a regra errada seria plausível e falso — por isso o gráfico fica vazio em vez de meio certo."
        />
      </Painel>

      <Painel className="mt-6">
        <CabecalhoGrafico
          titulo="Ponte do resultado"
          dica="Cascata do resultado orçado ao projetado no ano, explicando de onde vem o desvio. O AF-01 §10 trata a cascata como referência de experiência, não estrutura obrigatória."
        />
        <Vazio
          titulo="Sem as duas pontas, não há ponte"
          dica="A cascata parte do resultado orçado e chega ao projetado. Os dois dependem da regra do resultado na DRE, e o orçamento de 2026 não tem linha de receita."
        />
      </Painel>
    </div>
  );
}
